/**
 * Raise Alert feature — "Raise Alert" confirmation modal.
 *
 * Shown when the radiologist presses Submit and the scan finds one or more
 * positive critical findings in the report text. The radiologist ticks the
 * findings to raise, may add one manually via the "+" picker, then confirms.
 *
 * Findings come from the live scan (helpers/criticalAlert/criticalAlertService).
 * Confirming still persists through the existing single-valued raiseAlert field.
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Checkbox, Icon, IconButton } from "@fluentui/react";
import { t } from "i18next";

import ModalMain from "../Modal/ModalMain";
import ModalHeader from "../Modal/ModalHeader";
import ModalFooter from "../Modal/ModalFooter";
import themeContext from "../../context/theme/ThemeContext";
import { PriorityChip, MutedChip, EvidenceList } from "./CriticalAlertBits";
import { getPriorityMeta } from "./criticalAlertConstants";
import "./styles/CriticalAlertModals.css";

const PRIORITY_RANK = { CRITICAL: 0, URGENT: 1, HIGH_CONCERN: 2 };

/**
 * study.raise_alert is VARCHAR(45), and a free-text finding becomes that value
 * when it is the only one raised -- exactly as the old 5-option dropdown did.
 * Longer text would fail the insert outright under STRICT_TRANS_TABLES, so it is
 * capped here where the radiologist can still see what they typed.
 */
const FREE_TEXT_MAX = 45;

/**
 * A free-text finding has no rule behind it, so it has no alertCode. The modal
 * keys rows by alertCode, so one is synthesised for local use only -- the
 * payload sends alertCode: null, which is what study_critical_alert expects for
 * a manual entry.
 */
const keyOf = (finding) =>
  finding.alertCode || `free-text:${finding.findingName}`;

/**
 * @param {"SUBMIT"|"QC_PASS"|"PRELIMINARY"|"MANUAL"} mode
 *   SUBMIT  = opened by the Submit button (the scan found something)
 *   QC_PASS = opened by QC Pass. QC is the last checkpoint before the report
 *             reaches the clinician, so it is prompted too (Operations, Q2).
 *   PRELIMINARY = opened by Mark as Preliminary. A preliminary report is a
 *             release -- often the fastest route to the clinician in an
 *             emergency -- so it is prompted on the same principle. Radiologists
 *             and typists only: the same button reads "Save" for other roles,
 *             and a save is not a release.
 *   MANUAL  = opened by the Raise Alert button, may have no detections.
 */
const CriticalAlertConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  onSkip,
  detectedFindings = [],
  /* Manual findings the radiologist confirmed on an earlier pass. A scan can
   * re-derive every auto-detected finding from the report text; it can never
   * re-derive a manual one, so they are seeded back in here rather than being
   * silently dropped when a later scan replaces the stored set. They arrive
   * ticked, and can be removed like any other manual entry. */
  preConfirmedManual = [],
  catalogue = [],
  mode = "SUBMIT",
}) => {
  const { getThemeColor } = useContext(themeContext);

  // findings added by hand through the "+" picker
  const [manualFindings, setManualFindings] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  // every detected finding starts ticked
  const [uncheckedCodes, setUncheckedCodes] = useState([]);

  /* Closing the modal renders null but does NOT unmount this component, so
   * every piece of state below survived into the next open. That is what left
   * the picker showing instead of the "Add another finding" button, and it also
   * carried a manually added finding, and any unticked boxes, from one study
   * into the next one the radiologist opened. Reset on each open. */
  // what the radiologist has typed into the finding picker
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (isOpen) {
      // seeded, not empty: manual findings from an earlier pass carry forward
      setManualFindings(
        (Array.isArray(preConfirmedManual) ? preConfirmedManual : []).map((f) => ({
          ...f,
          isManual: true,
        }))
      );
      setShowPicker(false);
      setUncheckedCodes([]);
      setQuery("");
    }
  }, [isOpen]);

  const closePicker = () => {
    setShowPicker(false);
    setQuery("");
  };

  const primaryColor = getThemeColor("primary-color");
  const rowBg = getThemeColor("popup-background");
  const rowBorder = getThemeColor("border");

  const allFindings = useMemo(
    () =>
      [...detectedFindings, ...manualFindings].sort(
        (a, b) =>
          (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9)
      ),
    [detectedFindings, manualFindings]
  );

  const isChecked = (key) => !uncheckedCodes.includes(key);

  const selectedFindings = allFindings.filter((f) => isChecked(keyOf(f)));

  const toggleFinding = (key, checked) => {
    setUncheckedCodes((prev) =>
      checked ? prev.filter((c) => c !== key) : [...prev, key]
    );
  };

  const pickerOptions = useMemo(() => {
    const already = new Set(allFindings.map((f) => keyOf(f)));
    return catalogue
      .filter((item) => !already.has(item.alertCode))
      .map((item) => ({
        value: item.alertCode,
        label: `${item.findingName}  ·  ${getPriorityMeta(item.priority).label}`,
        raw: item,
      }));
  }, [allFindings, catalogue]);

  const trimmedQuery = query.trim();

  const filteredOptions = useMemo(() => {
    const needle = trimmedQuery.toLowerCase();
    const matches = needle
      ? pickerOptions.filter((o) =>
          o.raw.findingName.toLowerCase().includes(needle)
        )
      : pickerOptions;
    return matches.slice(0, 50); // 107 rules; the list scrolls, but keep it cheap
  }, [pickerOptions, trimmedQuery]);

  /* Offer free text once it is more than a stray keystroke and does not already
   * name a listed finding -- otherwise the radiologist would be invited to
   * hand-type something the rule library already covers, losing its priority. */
  const canAddFreeText =
    trimmedQuery.length > 1 &&
    !pickerOptions.some(
      (o) => o.raw.findingName.toLowerCase() === trimmedQuery.toLowerCase()
    ) &&
    !allFindings.some(
      (f) => (f.findingName || "").toLowerCase() === trimmedQuery.toLowerCase()
    );

  const handlePick = (option) => {
    if (!option) {
      return;
    }
    setManualFindings((prev) => [...prev, { ...option.raw, isManual: true }]);
    closePicker();
  };

  /* Typed text rather than a listed finding. The old Raise Alert dropdown let
   * the radiologist type their own wording alongside its 5 fixed options, and
   * that has to keep working: the rule library covers 107 findings but not
   * everything, and the alert matters more than the taxonomy. */
  const addFreeText = (text, priority) => {
    const name = String(text || "").trim().slice(0, FREE_TEXT_MAX);
    if (!name) {
      return;
    }
    setManualFindings((prev) => [
      ...prev,
      {
        alertCode: null,
        findingName: name,
        priority,
        isManual: true,
        isFreeText: true,
      },
    ]);
    closePicker();
  };

  const removeManual = (key) => {
    setManualFindings((prev) => prev.filter((f) => keyOf(f) !== key));
    setUncheckedCodes((prev) => prev.filter((c) => c !== key));
  };

  const handleConfirm = () => {
    onConfirm?.(selectedFindings);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  if (!isOpen) {
    return null;
  }

  const criticalCount = allFindings.filter(
    (f) => f.priority === "CRITICAL" && isChecked(keyOf(f))
  ).length;

  const modalDetails = {
    width: "620px",
    maxWidth: "94vw",
    header: (
      <ModalHeader
        updatedModal={true}
        heading={t("critical-alert.confirm-title", "Raise Alert")}
        handleCancel={onClose}
      />
    ),
    body: (
      <div className="ca-modal-body" style={{ color: primaryColor }}>
        <div
          className="ca-banner"
          style={{
            borderBottomColor: rowBorder,
            background:
              detectedFindings.length > 0
                ? getPriorityMeta("CRITICAL").softBg
                : "transparent",
          }}
        >
          <Icon
            iconName={detectedFindings.length > 0 ? "Warning" : "Info"}
            className="ca-banner-icon"
            style={{
              color:
                detectedFindings.length > 0
                  ? getPriorityMeta("CRITICAL").color
                  : primaryColor,
            }}
          />
          <div className="ca-banner-text">
            <p className="ca-banner-title">
              {detectedFindings.length > 0
                ? t(
                    "critical-alert.detected-title",
                    "{{count}} possible alert(s) detected in this report",
                    { count: detectedFindings.length }
                  )
                : t(
                    "critical-alert.none-detected-title",
                    "No alerts were detected automatically"
                  )}
            </p>
            <p className="ca-banner-sub">
              {detectedFindings.length > 0
                ? mode === "SUBMIT"
                  ? t(
                      "critical-alert.detected-sub",
                      "Select the findings you want to raise an alert for, then confirm to submit."
                    )
                  : mode === "QC_PASS"
                  ? t(
                      "critical-alert.detected-sub-qc",
                      "Select the findings you want to raise an alert for, then confirm to QC Pass."
                    )
                  : mode === "PRELIMINARY"
                  ? t(
                      "critical-alert.detected-sub-preliminary",
                      "Select the findings you want to raise an alert for, then confirm to mark preliminary."
                    )
                  : t(
                      "critical-alert.detected-sub-manual",
                      "Select the findings you want to raise an alert for."
                    )
                : t(
                    "critical-alert.none-detected-sub",
                    "You can still raise an alert yourself — add a finding below."
                  )}
            </p>
          </div>
        </div>

        <ul className="ca-list">
          {allFindings.map((finding) => {
            const meta = getPriorityMeta(finding.priority);
            return (
              <li
                key={keyOf(finding)}
                className="ca-row"
                style={{
                  background: rowBg,
                  borderColor: rowBorder,
                  borderLeftColor: meta.color,
                }}
              >
                <Checkbox
                  checked={isChecked(keyOf(finding))}
                  onChange={(e, checked) =>
                    toggleFinding(keyOf(finding), checked)
                  }
                  ariaLabel={finding.findingName}
                  styles={{
                    checkbox: { borderColor: meta.color },
                    checkmark: { color: "#ffffff" },
                  }}
                />

                <div className="ca-row-main">
                  <div className="ca-row-title-line">
                    <span
                      className="ca-finding-name"
                      style={{ color: primaryColor }}
                    >
                      {finding.findingName}
                    </span>
                    <PriorityChip priority={finding.priority} />
                    {finding.isManual && (
                      <MutedChip
                        text={t("critical-alert.added-manually", "Added manually")}
                        color={primaryColor}
                      />
                    )}
                  </div>

                  <EvidenceList
                    occurrences={finding.occurrences}
                    sentence={finding.matchedSentence}
                    matchedTerm={finding.matchedTerm}
                    accentColor={meta.color}
                  />
                </div>

                {finding.isManual && (
                  <div className="ca-row-remove">
                    <IconButton
                      iconProps={{ iconName: "Cancel" }}
                      title={t("button.remove", "Remove")}
                      ariaLabel={t("button.remove", "Remove")}
                      onClick={() => removeManual(keyOf(finding))}
                      styles={{
                        root: { height: 24, width: 24, background: "transparent" },
                        icon: { fontSize: 11, color: primaryColor },
                        rootHovered: { background: "transparent" },
                      }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <div className="ca-add-area">
          <div className="ca-add-row">
            {!showPicker ? (
              <button
                type="button"
                className="ca-add-button"
                style={{ color: primaryColor, borderColor: rowBorder }}
                onClick={() => setShowPicker(true)}
              >
                <Icon iconName="Add" style={{ fontSize: 12 }} />
                {t("critical-alert.add-finding", "Add another finding")}
              </button>
            ) : (
              <>
                {/* A plain text input, not a react-select. Its menu is absolutely
                    positioned, which meant it either escaped the modal (portalled)
                    or overflowed and flipped over the findings list (in place),
                    and long finding names forced the modal to scroll sideways.
                    The results below are ordinary flow content, so they cannot
                    overflow, cannot flip, and cannot outlive the modal. */}
                <input
                  type="text"
                  autoFocus
                  className="ca-add-input"
                  style={{
                    color: primaryColor,
                    borderColor: rowBorder,
                    background: rowBg,
                  }}
                  value={query}
                  maxLength={FREE_TEXT_MAX}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.stopPropagation();
                      closePicker();
                    }
                  }}
                  placeholder={t(
                    "critical-alert.search-findings",
                    "Search a finding, or type your own…"
                  )}
                />
                <IconButton
                  iconProps={{ iconName: "Cancel" }}
                  title={t("button.cancel", "Cancel")}
                  onClick={closePicker}
                  styles={{
                    root: { height: 28, width: 28, background: "transparent" },
                    icon: { fontSize: 12, color: primaryColor },
                    rootHovered: { background: "transparent" },
                  }}
                />
              </>
            )}
          </div>

          {showPicker && (
            <div
              className="ca-add-results"
              style={{ borderColor: rowBorder, background: rowBg }}
            >
              {/* Free text. The radiologist chooses the priority, because it is
                  the priority that drives the studylist badge and the analytics
                  reason -- the engine has no basis to guess one for wording it
                  has never seen, and defaulting would be a clinical claim. */}
              {canAddFreeText && (
                <div className="ca-freetext-row">
                  <span className="ca-freetext-label">
                    {t("critical-alert.add-free-text", 'Add "{{text}}" as', {
                      text: trimmedQuery,
                    })}
                  </span>
                  <span className="ca-freetext-chips">
                    {["CRITICAL", "URGENT", "HIGH_CONCERN"].map((p) => {
                      const pm = getPriorityMeta(p);
                      return (
                        <button
                          key={p}
                          type="button"
                          className="ca-freetext-chip"
                          style={{ color: pm.color, borderColor: pm.color }}
                          onClick={() => addFreeText(trimmedQuery, p)}
                        >
                          {pm.label}
                        </button>
                      );
                    })}
                  </span>
                </div>
              )}

              {filteredOptions.length > 0 ? (
                <ul className="ca-add-options">
                  {filteredOptions.map((option) => {
                    const pm = getPriorityMeta(option.raw.priority);
                    return (
                      <li key={option.value}>
                        <button
                          type="button"
                          className="ca-add-option"
                          style={{ color: primaryColor }}
                          onClick={() => handlePick(option)}
                        >
                          <span className="ca-add-option-name">
                            {option.raw.findingName}
                          </span>
                          <span
                            className="ca-add-option-priority"
                            style={{ color: pm.color }}
                          >
                            {pm.label}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                !canAddFreeText && (
                  <p className="ca-add-empty" style={{ color: primaryColor }}>
                    {t("critical-alert.no-matches", "No matching finding.")}
                  </p>
                )
              )}
            </div>
          )}

          {!showPicker && (
            <p className="ca-add-hint" style={{ color: primaryColor }}>
              {t(
                "critical-alert.add-hint",
                "Not everything is picked up automatically — you can add a finding yourself."
              )}
            </p>
          )}
          <p className="ca-notification-note" style={{ color: primaryColor }}>
            {t(
              "critical-alert.notification-note",
              "Note: Email and WhatsApp notifications are sent only if Send Report Submit Notification is enabled for this firm. Confirming here records the alert on the study regardless."
            )}
          </p>
        </div>
      </div>
    ),
    footer: (
      <ModalFooter
        containerClassName="ca-modal-footer"
        DefaultButtonList={[
          {
            title:
              mode === "SUBMIT"
                ? t("critical-alert.submit-without", "Submit without alert")
                : mode === "QC_PASS"
                ? t("critical-alert.qc-pass-without", "QC Pass without alert")
                : mode === "PRELIMINARY"
                ? t("critical-alert.preliminary-without", "Mark preliminary without alert")
                : t("button.cancel", "Cancel"),
            onclick: mode === "MANUAL" ? onClose : handleSkip,
          },
        ]}
        PrimaryButtonList={[
          {
            title:
              (mode === "SUBMIT"
                ? t("critical-alert.confirm-raise", "Confirm & raise alert")
                : mode === "QC_PASS"
                ? t("critical-alert.confirm-qc-pass", "Confirm & QC Pass")
                : mode === "PRELIMINARY"
                ? t("critical-alert.confirm-preliminary", "Confirm & mark preliminary")
                : t("critical-alert.raise-alert", "Raise alert")) +
              (selectedFindings.length > 0 ? ` (${selectedFindings.length})` : ""),
            onclick: handleConfirm,
            disabled: selectedFindings.length === 0,
          },
        ]}
      >
        <div className="ca-footer-summary" style={{ color: primaryColor }}>
          <span className="ca-footer-count">
            {t("critical-alert.selected-count", "Selected")}:{" "}
            <strong>{selectedFindings.length}</strong>
            {criticalCount > 0 && (
              <span style={{ color: getPriorityMeta("CRITICAL").color }}>
                {"  ·  "}
                {criticalCount} {getPriorityMeta("CRITICAL").label.toLowerCase()}
              </span>
            )}
          </span>
          <button
            type="button"
            className="ca-link-button"
            style={{ color: primaryColor }}
            onClick={() =>
              setUncheckedCodes(
                uncheckedCodes.length > 0
                  ? []
                  : allFindings.map((f) => keyOf(f))
              )
            }
          >
            {uncheckedCodes.length > 0
              ? t("critical-alert.select-all", "Select all")
              : t("critical-alert.clear-all", "Clear all")}
          </button>
        </div>
      </ModalFooter>
    ),
  };

  return (
    <ModalMain
      updatedModal={true}
      showModal={isOpen}
      scrollableBody={true}
      modalDetails={modalDetails}
    />
  );
};

export default CriticalAlertConfirmModal;